/**
 * Guide Video Modal Component
 *
 * 모달로 가이드 영상을 보여주는 컴포넌트
 * - 1분짜리 가이드 영상 재생
 * - 건너뛰기, 다시 보지 않기 버튼 제공
 * - 영상 완료 시 자동으로 모달 닫기
 * - 로컬 스토리지로 다시 보지 않기 설정 저장
 */
import { Volume2, VolumeX, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "~/core/components/ui/button";
import { Checkbox } from "~/core/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/core/components/ui/dialog";

interface GuideVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDontShowToday: (checked: boolean) => void;
  videoUrl: string;
  title?: string;
}

export function GuideVideoModal({
  isOpen,
  onClose,
  onDontShowToday,
  videoUrl,
  title = "서비스 가이드",
}: GuideVideoModalProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dontShowToday, setDontShowToday] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 영상 메타데이터 로드 시 duration 설정
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  // 영상 시간 업데이트
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // 영상 재생 시작
  const handlePlay = () => {
    setIsPlaying(true);
  };

  // 영상 일시정지
  const handlePause = () => {
    setIsPlaying(false);
  };

  // 영상 완료 시 자동으로 모달 닫기
  const handleEnded = () => {
    setIsPlaying(false);
    onClose();
  };

  // 모달이 열릴 때 자동 재생
  useEffect(() => {
    if (isOpen && videoRef.current) {
      // 음소거 상태로 자동 재생
      videoRef.current.muted = true;
      setIsMuted(true);
      videoRef.current.play().catch((error) => {
        console.log("자동 재생 실패:", error);
      });
    }
  }, [isOpen]);

  // 음소거 토글 핸들러
  const handleToggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // 체크박스 변경 핸들러
  const handleCheckboxChange = (checked: boolean) => {
    setDontShowToday(checked);
    onDontShowToday(checked);
  };

  // 시간 포맷팅 (초를 mm:ss 형식으로)
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // 진행률 계산
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-auto w-auto max-w-none overflow-hidden p-0">
        <DialogHeader className="p-3 pb-2">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-3 pb-3">
          {/* 영상 플레이어 */}
          <div className="relative overflow-hidden rounded-lg bg-black">
            <video
              ref={videoRef}
              className="h-auto max-h-[90vh] w-auto md:max-h-[70vh]"
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
              controls
              preload="metadata"
              autoPlay
              muted
            >
              <source src={videoUrl} type="video/mp4" />
              <source src={videoUrl} type="video/webm" />
              브라우저가 비디오를 지원하지 않습니다.
            </video>

            {/* 진행률 바 */}
            <div className="absolute right-0 bottom-0 left-0 h-1 bg-gray-600">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* 체크박스와 소리 듣기 버튼 */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="dont-show-today"
                checked={dontShowToday}
                onCheckedChange={handleCheckboxChange}
              />
              <label
                htmlFor="dont-show-today"
                className="cursor-pointer text-sm text-gray-700"
              >
                앞으로 보지 않기
              </label>
            </div>

            {/* 소리 듣기 버튼 */}
            <button
              onClick={handleToggleMute}
              className="flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-blue-600"
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
              <span>소리 듣기</span>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
